import { useEffect, useState } from "react";

/**
 * KC-E2 (Codex #2898): a tiny, ORG-SCOPED, shared "last clean sync" signal for
 * the offline-attendance queue.
 *
 * The connection badge needs to reflect whichever path actually reconciled the
 * queue — the GLOBAL drain (`useOfflineSync`, run by AppLayout's network banner)
 * OR a kiosk-local sync (`useOfflineAttendance`). Hook-local state can't see the
 * other path and isn't scoped to the active org, so the timestamp gets stale /
 * cross-org wrong. We persist it to `localStorage` (keyed by org) and notify
 * same-tab listeners via a custom event; cross-tab updates arrive via `storage`.
 *
 * "Clean" means the queue fully reconciled — no failures and nothing left
 * pending. A partial/failed sync CLEARS the signal so the badge never claims
 * "synced" while records remain queued for retry.
 */
const KEY_PREFIX = "forge:offline-last-clean-sync:";
const CHANGE_EVENT = "forge:offline-last-clean-sync";

const keyFor = (organizationId: string) => `${KEY_PREFIX}${organizationId}`;

/**
 * The global `localStorage` (=== `window.localStorage` in a browser), or null
 * when unavailable (SSR / private mode / disabled). Accessing it can throw, so
 * it's wrapped — a cosmetic timestamp must never break a sync path.
 */
function safeStorage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

function notifyChange(organizationId: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { organizationId } }));
}

/** Record a clean reconciliation for an org (called by both sync paths). */
export function setLastCleanSync(
  organizationId: string | null | undefined,
  when: Date = new Date(),
): void {
  if (!organizationId) return;
  const store = safeStorage();
  if (!store) return;
  try {
    store.setItem(keyFor(organizationId), when.toISOString());
    notifyChange(organizationId);
  } catch {
    // Storage write failed (quota) — the badge just won't show a time. Never
    // throw out of a sync path for a cosmetic timestamp.
  }
}

/** Clear the signal for an org (partial/failed sync, or records still pending). */
export function clearLastCleanSync(organizationId: string | null | undefined): void {
  if (!organizationId) return;
  const store = safeStorage();
  if (!store) return;
  try {
    store.removeItem(keyFor(organizationId));
    notifyChange(organizationId);
  } catch {
    // ignore — see setLastCleanSync.
  }
}

/** Read the last clean-sync time for an org, or null. */
export function getLastCleanSync(organizationId: string | null | undefined): Date | null {
  if (!organizationId) return null;
  const store = safeStorage();
  if (!store) return null;
  try {
    const raw = store.getItem(keyFor(organizationId));
    if (!raw) return null;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
}

/**
 * Reactive read of the active org's last clean-sync time. Re-reads on org change
 * and whenever either sync path writes (same-tab custom event) or another tab
 * updates the queue (storage event).
 */
export function useLastCleanSync(organizationId: string | null | undefined): Date | null {
  const [value, setValue] = useState<Date | null>(() => getLastCleanSync(organizationId));

  useEffect(() => {
    setValue(getLastCleanSync(organizationId));
    if (typeof window === "undefined" || !organizationId) return;

    const refresh = () => setValue(getLastCleanSync(organizationId));
    const onCustom = (event: Event) => {
      const detail = (event as CustomEvent<{ organizationId?: string }>).detail;
      if (!detail || detail.organizationId === organizationId) refresh();
    };
    const onStorage = (event: StorageEvent) => {
      // `key === null` fires on storage.clear(); refresh defensively.
      if (event.key === null || event.key === keyFor(organizationId)) refresh();
    };

    window.addEventListener(CHANGE_EVENT, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, [organizationId]);

  return value;
}
